import { Check } from 'lucide-react';
import type { EntityDTO } from '../../api/hooks.js';

// Portée par entité — « Toutes » ou n'importe quelle combinaison.
// Même vocabulaire visuel que les puces « Entités visibles » d'Accès &
// utilisateurs : une liste vide = toutes les entités.

interface Props {
  ents: EntityDTO[];
  value: string[]; // [] = toutes les entités
  // Un updater (et non une valeur calculée) : deux clics dans le même
  // tick de rendu liraient sinon le même `value` périmé et la seconde
  // entité écraserait la première.
  onChange: React.Dispatch<React.SetStateAction<string[]>>;
  testId: string;
  label: string;
  disabled?: boolean;
}

export function ScopePicker({ ents, value, onChange, testId, label, disabled }: Props) {
  const all = value.length === 0;
  return (
    <div className="scope-chips" role="group" aria-label={label} data-testid={testId}>
      <button
        type="button"
        className={'scope-chip' + (all ? ' sel' : '')}
        aria-pressed={all}
        disabled={disabled}
        onClick={() => !all && onChange([])}
        data-testid={`${testId}-all`}>
        <Check aria-hidden="true" /> Toutes
      </button>
      {ents.map((e) => {
        const sel = value.includes(e.id);
        return (
          <button
            key={e.id}
            type="button"
            className={'scope-chip ent' + (sel ? ' sel' : '') + (all ? ' dim' : '')}
            style={{ '--ec': e.color } as React.CSSProperties}
            aria-pressed={sel}
            disabled={disabled}
            title={e.name}
            onClick={() => onChange((prev) => (prev.includes(e.id) ? prev.filter((x) => x !== e.id) : [...prev, e.id]))}
            data-testid={`${testId}-${e.id}`}>
            <span className="edot" />{e.short}
          </button>
        );
      })}
    </div>
  );
}

// Les entités d'une portée, pour l'affichage. `ids` vide = « Toutes ».
export function scopeBadges(ids: Array<string | null>, ents: EntityDTO[]) {
  if (ids.some((id) => id === null)) return [{ key: 'all', cls: 'all', label: 'Toutes' }];
  return ids
    .filter((id): id is string => id !== null)
    .map((id) => {
      const e = ents.find((x) => x.id === id);
      return { key: id, cls: (e?.short ?? '').toLowerCase(), label: e?.short ?? '—' };
    });
}
